import { parseTradingMethodSpec } from "./trading-methods.ts";
import { normalizeStrategyEntry } from "./strategy-entry.ts";
import { isStrategyPresetName } from "./strategy-presets.ts";
import {
  backtestDateRange,
  backtestMarketForSymbol,
  backtestProviderForSymbol,
  promptForForm,
  type BacktestDateRange,
} from "./api.ts";
import type {
  BacktestFormState,
  BacktestSetupSnapshot,
  HistoryItem,
  StrategyMode,
} from "./types.ts";

export interface PreparedBacktestSnapshot {
  dateRange: BacktestDateRange;
  fingerprint: string;
  prompt: string;
  setup: BacktestSetupSnapshot;
}

/**
 * Cloud history keeps the canonical prompt at the item level. Older backend
 * normalizers may omit setup.prompt, so a manual rerun must never inherit the
 * prompt that happened to be in the currently open form.
 */
export function promptForHistoryItem(
  item: HistoryItem,
  resolvedStrategyMode: StrategyMode,
  currentPrompt: string,
): string {
  if (resolvedStrategyMode === "manual") return item.prompt;
  return item.setup.prompt || currentPrompt;
}

/**
 * Freezes all request-sensitive history evidence together. In particular, a
 * quick-range run that finishes after local midnight must retain the exact
 * date range that was sent when it started.
 */
export function prepareBacktestSnapshot(
  form: BacktestFormState,
  maxCandles: number,
  maxPeriodDays?: number,
  referenceDate = new Date(),
): PreparedBacktestSnapshot {
  const dateRange = backtestDateRange(
    form,
    maxCandles,
    maxPeriodDays,
    referenceDate,
  );
  const prompt = promptForForm(form);
  const setup: BacktestSetupSnapshot = {
    ftmoSimulation: form.ftmoSimulation,
    symbol: form.symbol,
    timeframe: form.timeframe,
    backtestPeriod: form.period,
    strategyMode: form.strategyMode,
    strategyName: form.strategyName,
    prompt,
    strategyDSL: form.strategyMode === "builder" ? form.strategyDSL : undefined,
    strategyMethod: form.strategyMode === "method" ? parseTradingMethodSpec(form.strategyMethod) ?? undefined : undefined,
    initialCapital: form.initialCapital,
    commissionPercent: form.commissionRate,
    slippageBps: form.slippageBps,
    positionSizePercent: form.positionSizePct,
    executionModel: form.executionModel,
    marginMode: form.marginMode,
    leverage: form.executionModel === "futures" ? form.leverage : 1,
    maintenanceMarginPercent: form.maintenanceMarginPercent,
    fundingRatePercent: form.executionModel === "futures" ? form.fundingRatePercent : 0,
    fundingIntervalHours: form.fundingIntervalHours,
    liquidationFeePercent: form.liquidationFeePercent,
    stopLossPercent: form.stopLossPct,
    takeProfitPercent: form.takeProfitPct,
    tradeDirection: form.tradeDirection,
    exitMode: form.exitMode,
    useAI: form.useAI,
    startDate: dateRange.start_date || null,
    endDate: dateRange.end_date || null,
    riskMode: form.unsupportedMethodExecution?.riskMode ?? (form.strategyMode === "method" ? "strategy" : "auto"),
    positionSizing: "notional",
    maxLeverage: 1,
    market: form.market ?? backtestMarketForSymbol(form.symbol),
    provider: form.provider ?? backtestProviderForSymbol(form.symbol),
  };
  return {
    dateRange,
    fingerprint: JSON.stringify({ prompt, setup }),
    prompt,
    setup,
  };
}

const timeframes = ["5m", "15m", "30m", "1h", "4h", "1d"] as const;
const periods = ["1M", "3M", "6M", "1Y", "Custom"] as const;
const executionModels = ["spot", "futures"] as const;
const marginModes = ["isolated", "cross"] as const;
const tradeDirections = ["long", "short", "both"] as const;
const exitModes = ["tp_sl_only", "signal_and_risk", "signal_only"] as const;

/** Use the same restoration policy for the Library CTA and its action. */
export function canPrepareHistoryRerun(current: BacktestFormState, item: HistoryItem): boolean {
  const restored = applyHistorySetup(current, item);
  return (restored.strategyMode === "method" && !restored.unsupportedMethodExecution && Boolean(parseTradingMethodSpec(restored.strategyMethod)))
    || restored.strategyMode === "auto"
    || (restored.strategyMode === "builder" && Boolean(restored.strategyDSL));
}

export function applyHistorySetup(current: BacktestFormState, item: HistoryItem): BacktestFormState {
  const setup = item.setup;
  const sourceMethod = parseTradingMethodSpec(setup.strategyMethod);
  const evidencePolicy = item.result?.method_evidence;
  const riskMode = setup.riskMode ?? (evidencePolicy?.risk_policy === "request_fixed_percent" ? "fixed_percent"
    : evidencePolicy?.risk_policy && !["frozen_structural_stop_rr_from_actual_fill", "frozen_atr_stop_rr_from_actual_fill"].includes(evidencePolicy.risk_policy) ? evidencePolicy.risk_policy : "strategy");
  const exitMode = setup.exitMode ?? evidencePolicy?.exit_policy ?? "tp_sl_only";
  const unsupportedMethodExecution = sourceMethod && (!["auto", "strategy"].includes(riskMode) || exitMode !== "tp_sl_only")
    ? { riskMode, exitMode } : undefined;
  const sourceRules = setup.strategyDSL ?? item.result?.strategy_builder?.canonical_dsl;
  const strategyName = setup.strategyName || current.strategyName;
  // Released saved text strategies may share a built-in display name. A name
  // alone cannot authorize replacing their source rules with today's preset.
  const sourcePrompt = (item.prompt || setup.prompt || "").trim();
  const matchesBuiltInPreset = isStrategyPresetName(setup.strategyName)
    && setup.useAI !== true
    && sourcePrompt === promptForForm({
      ...current,
      strategyMode: "auto",
      strategyName: setup.strategyName,
      strategyMethod: undefined,
      symbol: setup.symbol || current.symbol,
    });
  const strategyMode = sourceMethod ? "method"
    : setup.strategyMode === "method" || setup.strategyMethod !== undefined ? "manual"
    : setup.strategyMode === "manual"
    ? "manual"
    : setup.strategyMode === "builder" || sourceRules
      ? "builder"
      : matchesBuiltInPreset
        ? "auto"
        : "manual";
  return normalizeStrategyEntry({
    ...current,
    symbol: setup.symbol || current.symbol,
    provider: setup.provider,
    market: setup.market,
    timeframe: timeframes.includes(setup.timeframe as BacktestFormState["timeframe"])
      ? (setup.timeframe as BacktestFormState["timeframe"])
      : current.timeframe,
    // A saved quick-range run becomes a fixed custom range when reopened. That
    // preserves the exact candles instead of silently sliding "1M" to today.
    period: setup.startDate && setup.endDate
      ? "Custom"
      : periods.includes(setup.backtestPeriod as BacktestFormState["period"])
        ? (setup.backtestPeriod as BacktestFormState["period"])
        : current.period,
    strategyMode,
    strategyMethod: sourceMethod ?? undefined,
    ftmoSimulation: setup.ftmoSimulation,
    unsupportedMethodExecution,
    strategyName,
    prompt: promptForHistoryItem(item, strategyMode, current.prompt),
    strategyDSL: strategyMode === "builder" || strategyMode === "manual" ? sourceRules : undefined,
    initialCapital: setup.initialCapital ?? current.initialCapital,
    commissionRate: setup.commissionPercent ?? current.commissionRate,
    slippageBps: setup.slippageBps ?? current.slippageBps,
    positionSizePct: setup.positionSizePercent ?? current.positionSizePct,
    executionModel: executionModels.includes(setup.executionModel as BacktestFormState["executionModel"])
      ? (setup.executionModel as BacktestFormState["executionModel"])
      : "spot",
    marginMode: marginModes.includes(setup.marginMode as BacktestFormState["marginMode"])
      ? (setup.marginMode as BacktestFormState["marginMode"])
      : "isolated",
    leverage: setup.leverage ?? current.leverage,
    maintenanceMarginPercent: setup.maintenanceMarginPercent ?? current.maintenanceMarginPercent,
    fundingRatePercent: setup.fundingRatePercent ?? current.fundingRatePercent,
    fundingIntervalHours: setup.fundingIntervalHours ?? current.fundingIntervalHours,
    liquidationFeePercent: setup.liquidationFeePercent ?? current.liquidationFeePercent,
    stopLossPct: setup.stopLossPercent ?? current.stopLossPct,
    takeProfitPct: setup.takeProfitPercent ?? current.takeProfitPct,
    tradeDirection: tradeDirections.includes(setup.tradeDirection as BacktestFormState["tradeDirection"])
      ? (setup.tradeDirection as BacktestFormState["tradeDirection"])
      : current.tradeDirection,
    exitMode: exitModes.includes((sourceMethod ? exitMode : setup.exitMode) as BacktestFormState["exitMode"])
      ? ((sourceMethod ? exitMode : setup.exitMode) as BacktestFormState["exitMode"])
      : current.exitMode,
    useAI: setup.useAI ?? current.useAI,
    startDate: inputDateFromISO(setup.startDate) || current.startDate,
    endDate: inputDateFromISO(setup.endDate) || current.endDate,
    restoredDateRange: setup.startDate && setup.endDate && inputDateFromISO(setup.startDate) && inputDateFromISO(setup.endDate)
      ? { start: setup.startDate, end: setup.endDate, inputStart: inputDateFromISO(setup.startDate), inputEnd: inputDateFromISO(setup.endDate) }
      : undefined,
  }, setup.strategyMode === "method" || setup.strategyMethod !== undefined ? undefined : sourceRules);
}

function inputDateFromISO(value?: string | null): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
